import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtShort } from "@/lib/scheduling";
import { Composer, MessageBubble } from "@/pages/portal/PortalMessages";
import { AiAssist } from "@/components/AiAssist";

export const CaseConversation = ({ caseId, compact = false }) => {
  const qc = useQueryClient();
  const endRef = useRef(null);
  const [draft, setDraft] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["messages", "case", caseId], queryFn: async () => (await api.get(`/messages/cases/${caseId}`)).data, enabled: !!caseId, refetchInterval: 30_000 });
  const send = useMutation({
    mutationFn: (body) => api.post(`/messages/cases/${caseId}`, { body }),
    onSuccess: () => { toast.success("Reply sent. The person has been emailed that a message is waiting."); qc.invalidateQueries({ queryKey: ["messages"] }); qc.invalidateQueries({ queryKey: ["staff"] }); },
    onError: (e) => toast.error(apiError(e)),
  });
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [data?.messages?.length]);
  if (!caseId) return <p className="text-ink-500" data-testid="conversation-none">Choose a conversation.</p>;
  if (isLoading || !data) return <p className="text-ink-500" role="status">Loading conversation…</p>;
  return (
    <div data-testid={`conversation-${caseId}`}>
      {!compact && (
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-2xl">{data.case.person} <span className="font-sans text-base font-normal text-ink-500">· {data.case.case_code}</span></h2>
          <Link to={`/staff/cases/${caseId}`} className="link text-sm" data-testid="conversation-open-case">Open case</Link>
        </div>
      )}
      <ul className={`mt-4 space-y-3 ${compact ? "max-h-[420px]" : "max-h-[55vh]"} overflow-y-auto pr-1`} aria-label="Conversation">
        {data.messages.map((m) => <MessageBubble key={m.id} m={{ ...m, mine: m.sender_role === "staff" }} staffView />)}
        {data.messages.length === 0 && <li className="surface p-4 text-ink-500" data-testid="conversation-empty">No messages yet. Anything you write here reaches the person in their portal, with a Discreet email nudge.</li>}
        <li ref={endRef} aria-hidden="true" />
      </ul>
      <Composer onSend={(body, reset) => send.mutate(body, { onSuccess: reset })} pending={send.isPending} placeholder="Reply as the care team…" testPrefix="staff-msg" draft={draft} />
      {data.messages.length > 0 && <AiAssist path={`reply/${caseId}`} compact label="Draft A Reply With Claude" useLabel="Put In Reply Box" instructionPlaceholder="Optional: what should the reply say?" testId="ai-reply" onUse={(t) => setDraft({ text: t, at: Date.now() })} />}
    </div>
  );
};

export default function StaffMessages() {
  const [params, setParams] = useSearchParams();
  const selected = params.get("case");
  const { data: inbox = [], isLoading } = useQuery({ queryKey: ["messages", "inbox"], queryFn: async () => (await api.get("/messages/inbox")).data, refetchInterval: 30_000 });
  return (
    <div data-testid="staff-messages">
      <h1 className="text-4xl">Messages</h1>
      <p className="mt-3 max-w-[65ch] text-ink-500">Portal conversations with members. Each person has one running conversation with the care team; replies are emailed as a Discreet nudge only. Messages with crisis words also land in the Urgent queue.</p>
      <div className="mt-8 grid gap-8 lg:grid-cols-12">
        <aside className="lg:col-span-5" aria-label="Conversations">
          <ul className="divide-y divide-sage-400/50 border-y border-sage-400/50" data-testid="messages-inbox">
            {inbox.map((c) => (
              <li key={c.case_id}>
                <button type="button" onClick={() => setParams({ case: c.case_id })} aria-current={selected === c.case_id ? "true" : undefined} className={`w-full px-3 py-3 text-left hover:bg-cream-100 ${selected === c.case_id ? "bg-cream-100 border-l-4 border-olive-900" : "border-l-4 border-transparent"}`} data-testid={`inbox-${c.case_id}`}>
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-semibold">{c.person}</span><span className="text-sm text-ink-500">· {c.case_code}</span>
                    {c.unread > 0 && <span className="ml-auto border border-olive-900 bg-olive-900 px-2 py-0.5 text-xs font-semibold text-cream-50" data-testid={`inbox-unread-${c.case_id}`}>{c.unread} new</span>}
                    {c.crisis && <span className="border border-terracotta-600 px-2 py-0.5 text-xs font-semibold text-terracotta-600">Crisis words</span>}
                  </div>
                  <p className="mt-1 truncate text-sm text-ink-500">{c.last_from === "member" ? "" : "You: "}{c.preview}</p>
                  <p className="mt-1 text-xs text-ink-500">{fmtShort(c.last_at)}{c.awaiting_reply ? " · awaiting reply" : ""}</p>
                </button>
              </li>
            ))}
            {!isLoading && inbox.length === 0 && <li className="py-6 text-ink-500" data-testid="inbox-empty">No conversations yet. Members write from Messages in their portal.</li>}
          </ul>
        </aside>
        <section className="lg:col-span-7">{selected ? <CaseConversation key={selected} caseId={selected} /> : <p className="surface p-5 text-ink-500" data-testid="conversation-none">Choose a conversation to read and reply.</p>}</section>
      </div>
    </div>
  );
}
